import axios from "axios";

import { API_URL, URL } from "../env.json";

// --- Constants ---
// Ensure these map to your Web environment variables (e.g., process.env or import.meta.env)
const baseUrl = API_URL || "";
const authUrl = URL || "";

export const STORAGE_KEYS = {
  JWT: "jwt",
  USER_ID: "id",
  USER: "user",
};

// --- Interfaces ---
export interface UserData {
  jwt: string;
  id: string | number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: number;
  org_id: number;
  org_name: string;
  profile_pic?: string;
  active_location: any;
  custom_json: any;
  association: any;
  activeUser: any;
  notification_subscription: boolean;
  status?: number;
}

export interface GrowthReportProps {
  // Define properties as needed for your project
  [key: string]: any;
}

// --- Helper Utilities ---
const getJson = (val: any) => (val ? JSON.parse(val) : []);
const checkNull = (val: any) => val !== null;

const parseObject = (element: any) => ({
  id: element.userId,
  org_id: element.schoolId,
  firstName: element.name,
  lastName: "",
  gender: element.gender,
  dob: element.dob,
  org_name: element.schoolName,
  role: element.role_type_id,
  hsg_id: element.uuId,
  class: element.grade,
});

const updateDefaultUser = (item: any) => ({
  id: item.id,
  role: item.role,
  org_id: item.org_id,
  firstName: item.firstName,
  lastName: item.lastName,
  gender: item.gender,
  dob: item.dob,
  org_name: item.org_name,
  class: item.class,
  hsg_id: item.hsg_id,
});

// --- API Calls ---

async function getUserDetails(id: string | number, token: string) {
  const response = await axios.get(`${baseUrl}user`, {
    headers: {
      authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-select": JSON.stringify({
        userid: id,
        email: true,
        profile: true,
        role: true,
        phone: true,
        custom: true,
        organization: true,
      }),
    },
  });
  return response.data;
}

async function getAssociationDetails(id: string | number, token: string) {
  const response = await axios.get(`${baseUrl}parent/student/association`, {
    headers: {
      authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-select": JSON.stringify({ sourceUserId: id }),
    },
  });
  return response.data;
}

export default async function loginApi(
  username: string,
  password: string
): Promise<UserData | any> {
  try {
    const loginRes = await axios.post(authUrl, { username, password });
    const { data: authData } = loginRes;

    if (authData.role?.[0]?.role_type_id === 4) {
      return { status: 405 };
    }

    const [userDetails, associationRes] = await Promise.all([
      getUserDetails(authData.id, authData.jwt),
      getAssociationDetails(authData.id, authData.jwt),
    ]);

    const rawUser = userDetails.results[0];
    const profile = getJson(rawUser.profile)[0] || {};
    const customData = getJson(rawUser.custom)[0] || {};

    const user: any = {
      jwt: authData.jwt,
      id: authData.id,
      username: authData.username,
      json_value: customData.json_value || {},
      firstName: profile.firstname || "",
      lastName: profile.lastname || "",
      role: getJson(rawUser.role)[0]?.role_type_id,
      org_id: getJson(rawUser.organization)[0]?.org_id || 319,
      org_name: getJson(rawUser.organization)[0]?.organization_name || "",
      email: checkNull(rawUser.email)
        ? getJson(rawUser.email)[0]?.user_email
        : "",
      phone: checkNull(rawUser.phone)
        ? getJson(rawUser.phone)[0]?.user_phone_number
        : "",
      profile_pic: profile.profile_pic,
      custom_json: customData.json_value || "",
      notification_subscription:
        customData.json_value?.notification_subscription ?? true,
      active_location: customData.json_value?.active_location || {
        country: "India",
        state: "",
        district: "",
        stateCode: "",
      },
    };

    if (associationRes.results[0]?.association) {
      const associationData = getJson(
        associationRes.results[0].association
      ).map(parseObject);
      user.association = associationData;
      user.activeUser = updateDefaultUser(associationData[0]);
    } else {
      user.association = "";
      user.activeUser = {
        id: user.id,
        role: user.role,
        org_id: user.org_id,
        firstName: user.firstName,
        lastName: user.lastName,
        gender: profile.gender,
        dob: profile.dob,
        org_name: user.org_name,
      };
    }

    // Save to LocalStorage for Web
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEYS.JWT, user.jwt);
    localStorage.setItem(STORAGE_KEYS.USER_ID, user.id.toString());

    return user;
  } catch (error) {
    console.error("Login API Error:", error.message);
    return error.response?.data || { error: "Login failed" };
  }
}

interface FetchReportParams {
  activity_id: string | number;
  jwt: string;
  student_id: string | number;
  event_track_id: string | number;
}

export async function fetchReportData({
  activity_id,
  jwt,
  student_id,
  event_track_id,
}: FetchReportParams): Promise<GrowthReportProps[]> {
  const myInit = {
    in_user_id: student_id,
    in_event_track_id: event_track_id,
    in_activity_id: activity_id,
  };

  const uri = `https://api.healthsetgo.com/api/v2/user/assessment/reports`;

  const res = await axios.post(uri, myInit, {
    headers: {
      authorization: "Bearer " + jwt,
    },
  });

  return res.data;
}

export async function getAssessmentPrograms({
  jwt,
  student_id,
}: {
  jwt: string;
  student_id: string;
}) {
  const api = "user/assessment/programs";
  try {
    const response = await axios.post(
      baseUrl + api,
      { in_user_id: student_id },
      { headers: { authorization: "Bearer " + jwt } }
    );
    return response.data;
  } catch (err) {
    return err.response.data;
  }
}
